import cloneDeep from 'lodash.clonedeep';

import {
  GET_USER_AUDIO_FAILURE,
  GET_USER_AUDIO_REQUESTED,
  GET_USER_AUDIO_SUCCESS,
  GetUserAudioFailureAction,
  GetUserAudioSuccessAction,
  SAVE_AUDIO_ASSET_METADATA_SUCCESS,
  SAVE_AUDIO_TO_LIBRARY_SUCCESS,
  SaveAudioAssetMetadataSuccessAction,
  SaveAudioToLibrarySuccessAction,
  OpenAudioModalAction,
  CloseAudioModalAction,
  OPEN_AUDIO_MODAL,
  CLOSE_AUDIO_MODAL,
  OpenDeleteAudioAssetModalAction,
  CloseDeleteAudioAssetModalAction,
  OPEN_DELETE_AUDIO_ASSET_MODAL,
  CLOSE_DELETE_AUDIO_ASSET_MODAL,
  UseAudioAssetInProjectSuccessAction,
  USE_AUDIO_ASSET_IN_PROJECT_SUCCESS,
  ConfirmAudioSelectionAction,
  CONFIRM_AUDIO_SELECTION,
  ChangeAudioModalTabAction,
  CHANGE_AUDIO_MODAL_TAB
} from 'js/actionCreators/audioActions';
import { AudioState, VSCAssetAudioSourceName } from 'js/types';

type AudioActionTypes =
  | GetUserAudioFailureAction
  | GetUserAudioSuccessAction
  | SaveAudioToLibrarySuccessAction
  | SaveAudioAssetMetadataSuccessAction
  | OpenAudioModalAction
  | CloseAudioModalAction
  | OpenDeleteAudioAssetModalAction
  | CloseDeleteAudioAssetModalAction
  | UseAudioAssetInProjectSuccessAction
  | ConfirmAudioSelectionAction
  | ChangeAudioModalTabAction
  | { type: typeof GET_USER_AUDIO_REQUESTED };

export const initialState: AudioState = {
  userAudioLoading: false,
  userAudioAssets: [],
  audioModalTab: null,
  audioModalInitialAction: null,
  deleteAudioAssetModalOpen: false,
  deleteAudioAssetId: null,
  selectedAudioClip: null,
  audioModalMode: null,
  recordingProjectScript: undefined
};

export default function audioReducer(state = initialState, action: AudioActionTypes): AudioState {
  switch (action.type) {
    case GET_USER_AUDIO_REQUESTED: {
      return {
        ...state,
        userAudioLoading: true
      };
    }

    case GET_USER_AUDIO_FAILURE: {
      return {
        ...state,
        userAudioLoading: false
      };
    }

    case GET_USER_AUDIO_SUCCESS: {
      // Map the audio role from the source if the audio role has not explicitly been set.
      const userAudioAssets = action.payload.map(audioAsset => {
        let audioRole = audioAsset.audioRole;
        if (!audioRole) {
          if (audioAsset.sourceName === VSCAssetAudioSourceName.USER_RECORDING) {
            audioRole = 'voiceover';
          } else if (audioAsset.sourceName === VSCAssetAudioSourceName.USER_UPLOAD) {
            audioRole = 'music';
          } else if (audioAsset.sourceName === VSCAssetAudioSourceName.SPARKOL_AUDIO) {
            audioRole = 'music';
          } else if (audioAsset.sourceName === VSCAssetAudioSourceName.SPARKOL_AI_GENERATED) {
            audioRole = 'voiceover';
          }
        }

        return {
          ...audioAsset,
          audioRole
        };
      });

      return {
        ...state,
        userAudioLoading: false,
        userAudioAssets: userAudioAssets
      };
    }

    case SAVE_AUDIO_TO_LIBRARY_SUCCESS: {
      return {
        ...state,
        userAudioAssets: [...state.userAudioAssets, action.audioClipData]
      };
    }

    case SAVE_AUDIO_ASSET_METADATA_SUCCESS: {
      const userAudioAssets = cloneDeep(state.userAudioAssets);
      const updateIndex = userAudioAssets.findIndex(
        asset => asset.projectAssetId === action.audioClipData.projectAssetId
      );

      if (updateIndex !== -1) {
        userAudioAssets[updateIndex] = action.audioClipData;
        return {
          ...state,
          userAudioAssets
        };
      }

      return state;
    }

    case OPEN_AUDIO_MODAL: {
      const { audioRole, projectScript } = action;

      return {
        ...state,
        audioModalTab: action.tab,
        audioModalInitialAction: action.initialAction,
        ...(action.selectedAudioClip && { selectedAudioClip: action.selectedAudioClip }),
        audioModalMode: audioRole,
        recordingProjectScript: projectScript
      };
    }

    case CHANGE_AUDIO_MODAL_TAB: {
      return {
        ...state,
        audioModalTab: action.tab
      };
    }

    case CONFIRM_AUDIO_SELECTION:
    case USE_AUDIO_ASSET_IN_PROJECT_SUCCESS:
    case CLOSE_AUDIO_MODAL: {
      return {
        ...state,
        audioModalTab: null,
        selectedAudioClip: null,
        audioModalMode: null,
        recordingProjectScript: undefined
      };
    }

    case OPEN_DELETE_AUDIO_ASSET_MODAL: {
      return {
        ...state,
        deleteAudioAssetModalOpen: true,
        deleteAudioAssetId: action.assetId
      };
    }

    case CLOSE_DELETE_AUDIO_ASSET_MODAL: {
      return {
        ...state,
        deleteAudioAssetModalOpen: false,
        deleteAudioAssetId: null
      };
    }

    default:
      return state;
  }
}
